import React, { useContext, useEffect } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  Pagination,
  PaginationCompact,
  PaginationDisplayedItemCount
} from '@one-thd/sui-atomic-components';
import { CreateAList } from '@thd-olt-component-react/list-forms';
import { ListTile } from '@thd-olt-component-react/list-tile';
import { EmptyMessagingB2C } from './empty-messaging/EmptyMessagingB2C';
import { EmptyMessagingB2B } from './empty-messaging/EmptyMessagingB2B';
import { dataModel } from './dataModel';
import { useLists } from './useLists';
import { LoadingUserLists } from './LoadingUserLists';

const { func, number, string, bool } = proptypes;

const NUMBER_PLACEHOLDERS = 6;

export const UserLists = ({ onPageChange, page, iconSize }) => {
  const { customer, isConsumerApp, channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isB2B = customer.type === 'b2b';
  const {
    lists, loading, error, refetchLists, pagination, variables
  } = useLists({ page });
  const { totalPages, totalResults, pageSize } = pagination || {};

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('user-lists.ready'); }, []);

  const reloadList = (event) => {
    if (event.target.visibilityState === 'visible') {
      refetchLists({ page });
    }
  };

  useEffect(() => {
    if (isConsumerApp) {
      window.addEventListener('visibilitychange', reloadList);
    }
  }, []);

  const handleListCreation = (newList) => {
    // Trigger analytics event when list is successfully created
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('list-forms.create-without-products');
    }

    const id = newList?.id;
    if (id) {
      window.location.href = `/list/view/details/${id}`;
    }
  };

  const onDelete = () => {
    refetchLists({ page });
  };

  const onChangeWrapper = (event, pageNo) => {
    event.preventDefault();
    if (onPageChange) {
      onPageChange(pageNo);
    }
  };

  return (
    <div className="sui-grid sui-grid-cols-4 sui-items-center sui-gap-7" data-component="UserLists">
      <div className="sui-col-span-4 md:sui-col-start-4 md:sui-col-end-5">
        <CreateAList onCreate={handleListCreation} />
      </div>
      <div className="sui-col-span-4" data-testid="user-lists">
        {(!lists?.length && !loading && isB2B) && (
          <EmptyMessagingB2B />
        )}
        {(!lists?.length && !loading && !isB2B) && (
          <EmptyMessagingB2C />
        )}
        {
          (!lists?.length && loading) && (<LoadingUserLists numberPlaceholders={NUMBER_PLACEHOLDERS} />)
        }
        {(lists.length > 0) && (
          <div className="sui-grid sui-gap-3 sui-grid-cols-1 md:sui-grid-cols-2
          lg:sui-grid-cols-3"
          >
            {
              lists?.map((list, listIndex) => (
                <ListTile
                  iconSize={iconSize}
                  key={listIndex + list.id}
                  listId={list.id}
                  onDelete={onDelete}
                  data={list}
                  numberOfThumbnails={3}
                  viewMode='thumbnails'
                  position={listIndex}
                />
              ))
            }
          </div>
        )}
      </div>
      <>
        {
          (!!totalPages && !isMobile) && (
            <div className="sui-col-span-4 sui-flex sui-justify-center sui-items-center" data-testid="lists-pagination">
              <PaginationDisplayedItemCount
                page={page}
                itemsPerPage={pageSize}
                items={totalResults}
              />
              {totalResults > 12 && (
                <Pagination
                  page={page}
                  onChange={onChangeWrapper}
                  count={totalPages}
                />
              )}
            </div>
          )
        }
        {
          (!!totalPages && isMobile) && (
            <div className="sui-col-span-4 sui-flex sui-flex-col sui-items-center" data-testid="lists-pagination">
              {totalResults > 12 && (
                <PaginationCompact
                  page={page}
                  onChange={onChangeWrapper}
                  count={totalPages}
                />
              )}
              <div className="sui-mt-2">
                <PaginationDisplayedItemCount
                  page={page}
                  itemsPerPage={pageSize}
                  items={totalResults}
                />
              </div>
            </div>
          )
        }
      </>
      {(!!lists?.length && lists?.length > 8 && !loading) && (
        <div className="sui-col-span-4 md:sui-col-start-4 md:sui-col-end-5" data-testid="bottom-create-new-list">
          <CreateAList onCreate={handleListCreation} />
        </div>
      )}
    </div>
  );
};

UserLists.propTypes = {
  onPageChange: func.isRequired,
  page: number,
  iconSize: string
};

UserLists.defaultProps = {
  page: 1,
  iconSize: 'small'
};

UserLists.displayName = 'UserLists';

UserLists.dataModel = extend(dataModel, CreateAList);
